/**
 * The stops on the history slider, and which one is showing.
 *
 * WHY THE STOPS ARE MOMENTS AND NOT MINUTES. A document did not exist continuously -- it existed at
 * the moments somebody saved it. A slider mapped to elapsed TIME would spend most of its travel in
 * the gaps between edits, where there is nothing to show, and would make a document that was
 * written in two bursts a week apart almost unusable. One stop per recorded moment puts the travel
 * where the history is.
 *
 * OLDEST ON THE LEFT, because that is the direction time reads, and the version list is newest
 * first -- so the index has to be turned over rather than used as it comes. Getting that wrong
 * gives a slider that works perfectly and runs backwards.
 *
 * THE LAST STOP IS NOW. The right-hand end is the live document rather than the newest recorded
 * moment: a reader who drags to the end expects to be back in the document, not looking at a
 * read-only copy of something that happens to be identical to it.
 */

export type Stop = {
    /** The version this stop shows, or null for the live document at the right-hand end. */
    version: any | null;
    /** Where it sits on the slider. */
    index: number;
};

/** The stops, oldest first, with `now` appended. */
export function stopsFor(versions: any[] | null | undefined): Stop[] {
    const ordered = [...(versions ?? [])].reverse();          // newest-first in, oldest-first out
    const stops: Stop[] = ordered.map((version, index) => ({ version, index }));
    stops.push({ version: null, index: stops.length });
    return stops;
}

/**
 * Where the slider should sit: the moment being viewed, or the far right when none is.
 *
 * MATCHED ON USID, not on position in the array. The list reloads after a name or a restore and the
 * same moment can land at a different index; matching by position would move the handle under the
 * reader's hand because something unrelated was added above it.
 */
export function positionOf(stops: Stop[], viewingUSID: string | null | undefined): number {
    if (!viewingUSID) return Math.max(0, stops.length - 1);
    const found = stops.findIndex(stop => stop.version?.USID === viewingUSID);
    return found >= 0 ? found : Math.max(0, stops.length - 1);
}

/**
 * The stops worth marking on the rail.
 *
 * NOT EVERY STOP. A procedure written over a week has hundreds of moments and a tick for each is a
 * grey smear that tells a reader nothing. The landmarks are the ones a person would recognise as
 * somewhere to go back to: a named version, and anything that was not ordinary typing -- a restore,
 * an import, the document being created.
 */
export function landmarksFor(stops: Stop[]): { value: number; label?: string }[] {
    return stops
        .filter(stop => stop.version
            && (stop.version.VersionName || (stop.version.Origin && stop.version.Origin !== "EDIT")))
        .map(stop => ({ value: stop.index, label: stop.version.VersionName || undefined }));
}
